import {
  createNewCustomer,
  signUserInWithEmailAndPassword,
  mapAuthError,
  useCustomerSessionPersistence,
} from "./firebase-authentication";
import {
  saveNewCustomerToDatabase,
  isCustomerRegisteredWithBusiness,
  getBusinessData,
} from "./firebase-database";
import { generateIOSRewardsCard, generateAndroidRewardsCard } from "./firebase-functions";
import { detectPlatform, passTypeForPlatform } from "./utils/platform.js";
import { track, identify } from "./analytics.js";

export { detectPlatform };

// Error code completeAuthentication reports when the email belongs to an
// existing account but the password did not match. Kept as the historical
// Firebase code so the NEEDS_LOGIN contract with the form is unchanged.
const WRONG_PASSWORD_FOR_EXISTING_ACCOUNT = "auth/email-already-in-use";

// `options.marketingConsent` is the separate marketing box on the form; it is
// false unless the customer ticked it.
export const initiatePassGeneration = async (
  customerEmail,
  businessId,
  password,
  options = {}
) => {
  const marketingConsent = Boolean(options.marketingConsent);
  track("enrollment_started", { business_id: businessId });
  try {
    // Auth and DB errors still throw as before - only pass generation is
    // wrapped in its own try/catch below.
    const { isNewAccount } = await completeAuthentication(
      customerEmail,
      password
    );
    identify(customerEmail);
    track("enrollment_authenticated", {
      business_id: businessId,
      is_new_account: isNewAccount,
    });

    // The business reads and the "already enrolled?" check are independent, so
    // they run together; the save then needs a single round trip.
    const [
      { businessData, thresholdForRedeeming, startStampCount },
      alreadyRegistered,
    ] = await Promise.all([
      getBusinessData(businessId),
      isCustomerRegisteredWithBusiness(customerEmail, businessId),
    ]);
    // Credited before the pass is built, so the wallet card the customer walks
    // away with already shows their head start.
    await saveNewCustomerToDatabase(
      customerEmail,
      businessId,
      thresholdForRedeeming,
      startStampCount,
      { alreadyRegistered, marketingConsent }
    );

    const brandColors = {
      foregroundColor: businessData?.foregroundColor,
      backgroundColor: businessData?.backgroundColor,
    };

    const platform = detectPlatform();
    const passType = passTypeForPlatform(platform);

    // From here on the customer record already exists. A failure to build the
    // pass must NOT throw - we hand the error to the caller so the completed
    // screen can offer a retry.
    let passUrl = null;
    let passGenError = null;
    // iOS only: whether we could positively confirm the pass built before
    // handing the URL to a navigation. Android generation is confirmed by
    // construction, since the save URL only exists once the pass is built.
    // Watch this in analytics - a run of `false` means the confirmation probe
    // is being blocked (most likely CORS on downloadIOSPass) and iOS is
    // silently back to its old, unverified behaviour.
    let passVerified = true;
    try {
      if (passType === "android") {
        const qrCodeData = `${customerEmail}|${businessData.id}|android`;
        const result = await generateAndroidRewardsCard(qrCodeData);
        passUrl = result.passUrl;
      } else {
        const result = await generateIOSRewardsCard(customerEmail, businessId);
        passUrl = result.passUrl;
        passVerified = result.verified;
      }
      track("enrollment_completed", {
        business_id: businessId,
        pass_type: passType,
        pass_verified: passVerified,
        marketing_consent: marketingConsent,
      });
    } catch (passError) {
      passGenError = passError.message || "Pass generation failed";
      track("enrollment_pass_gen_failed", {
        business_id: businessId,
        pass_type: passType,
        error_message: passGenError,
      });
    }

    return {
      passType,
      passUrl,
      passGenError,
      businessId,
      email: customerEmail,
      brandColors,
    };
  } catch (error) {
    if (error.code === WRONG_PASSWORD_FOR_EXISTING_ACCOUNT) {
      throw new Error("NEEDS_LOGIN");
    }
    const mappedError = mapAuthError(error);
    track("enrollment_failed", { business_id: businessId, error_message: mappedError.message });
    throw mappedError;
  }
};

// Most enrolments are brand-new accounts, so the account is created first and
// sign-in is only attempted when Firebase reports the email already exists.
// The previous order (sign in, then create on failure) made every new customer
// pay a guaranteed-to-fail sign-in round trip before the create. Outcomes are
// identical for every case:
//   - new email              -> account created (isNewAccount: true)
//   - existing + right pw    -> signed in        (isNewAccount: false)
//   - existing + wrong pw    -> NEEDS_LOGIN (wrong-password dialog)
//   - weak / invalid input   -> the same mapped Firebase error as before
const completeAuthentication = async (customerEmail, password) => {
  // Scope the customer's auth session to this tab only - it must never persist
  // into a long-lived authenticated state the way business staff sessions do.
  await useCustomerSessionPersistence();

  try {
    const userCredential = await createNewCustomer(customerEmail, password);
    track("account_created", { method: "email" });
    return { uid: userCredential.user.uid, isNewAccount: true };
  } catch (createError) {
    if (createError.code !== "auth/email-already-in-use") {
      const mappedError = mapAuthError(createError);
      track("sign_in_failed", { error_code: mappedError.code, error_message: mappedError.message });
      throw mappedError;
    }
  }

  try {
    const userCredential = await signUserInWithEmailAndPassword(customerEmail, password);
    track("sign_in", { method: "email" });
    return { uid: userCredential.user.uid, isNewAccount: false };
  } catch (signInError) {
    if (
      signInError.code === "auth/invalid-credential" ||
      signInError.code === "auth/wrong-password"
    ) {
      // The account exists (create just said so) and the password is wrong.
      const wrongPassword = new Error(signInError.message);
      wrongPassword.code = WRONG_PASSWORD_FOR_EXISTING_ACCOUNT;
      throw wrongPassword;
    }
    const mappedError = mapAuthError(signInError);
    track("sign_in_failed", { error_code: mappedError.code, error_message: mappedError.message });
    throw mappedError;
  }
};
